import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, extractError } from "../lib/api";
import { SectionCard, Pill } from "./Ui";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { formatListDate } from "../lib/purchaseListMeta";
import { Loader2, Plus, Truck, X } from "lucide-react";
import {
  sanitizeVehicleNumberInput,
  isValidVehicleNumber,
  VEHICLE_NUMBER_FORMAT_MSG,
  VEHICLE_NUMBER_PLACEHOLDER,
} from "../lib/vehicleNumber";

const NONE = "__none__";

function buildRowFromContract(c) {
  return {
    sales_contract_id: Number(c.id),
    expected_weight_qtl: "",
    contract_number: c.contract_number,
    variety: c.variety,
    item_name: c.item_name || c.variety,
    remaining_headroom_qtl: c.remaining_headroom_qtl,
  };
}

export default function OutwardTruckBookingsPanel({ contracts = [], onChanged }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    vehicle_number: "",
    driver_name: "",
    driver_phone: "",
    transporter_name: "",
    expected_arrival: "",
    loading_notes: "",
  });
  const [rows, setRows] = useState([]);

  const activeContracts = useMemo(
    () => (contracts || []).filter((c) => String(c.status || "").toUpperCase() === "ACTIVE"),
    [contracts],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/outward-truck-bookings");
      setBookings(data?.results || []);
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggleContract = (c) => {
    const id = String(c.id);
    setRows((prev) => {
      if (prev.some((r) => String(r.sales_contract_id) === id)) {
        return prev.filter((r) => String(r.sales_contract_id) !== id);
      }
      return [...prev, buildRowFromContract(c)];
    });
  };

  const updateRow = (id, patch) => {
    setRows((prev) =>
      prev.map((r) => (String(r.sales_contract_id) !== String(id) ? r : { ...r, ...patch })),
    );
  };

  const resetForm = () => {
    setForm({
      vehicle_number: "",
      driver_name: "",
      driver_phone: "",
      transporter_name: "",
      expected_arrival: "",
      loading_notes: "",
    });
    setRows([]);
    setOpen(false);
  };

  const submit = async () => {
    const vehicle = sanitizeVehicleNumberInput(form.vehicle_number);
    if (!isValidVehicleNumber(vehicle)) {
      toast.error(VEHICLE_NUMBER_FORMAT_MSG);
      return;
    }
    if (!rows.length) {
      toast.error("Select at least one sales contract");
      return;
    }
    for (const r of rows) {
      const wt = Number(r.expected_weight_qtl);
      if (!wt || wt <= 0) {
        toast.error(`Enter expected weight (qtl) for ${r.contract_number}`);
        return;
      }
    }
    setBusy(true);
    try {
      const allocations = rows.map((r) => ({
        sales_contract_id: Number(r.sales_contract_id),
        expected_weight_qtl: Number(r.expected_weight_qtl),
        planned_qtl: Number(r.expected_weight_qtl),
      }));
      await api.post("/outward-truck-bookings", {
        vehicle_number: vehicle,
        driver_name: form.driver_name.trim(),
        driver_phone: form.driver_phone.trim(),
        transporter_name: form.transporter_name.trim(),
        expected_arrival: form.expected_arrival,
        loading_notes: form.loading_notes.trim(),
        sales_contract_id: allocations[0].sales_contract_id,
        contract_allocations: allocations,
      });
      toast.success("Truck booked — gate will auto-link on arrival");
      resetForm();
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBusy(false);
    }
  };

  const cancelBooking = async (id) => {
    setBusy(true);
    try {
      await api.post(`/outward-truck-bookings/${id}/cancel`, null, { params: { reason: "Cancelled by sales" } });
      toast.success("Booking cancelled");
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <SectionCard title="Upcoming trucks" testId="outward-truck-bookings">
      <p className="text-xs text-muted-foreground mb-3">
        Book a transporter before gate entry. Matching plates auto-link sales on outward gate pass (expires after 3 days).
      </p>
      {!open ? (
        <Button type="button" size="sm" variant="outline" onClick={() => setOpen(true)} disabled={busy}>
          <Plus className="h-4 w-4 mr-1" /> Book vehicle
        </Button>
      ) : (
        <div className="rounded-xl border border-border p-4 space-y-4 mb-4">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-medium">New truck booking</p>
            <Button type="button" variant="ghost" size="icon" onClick={resetForm} aria-label="Close">
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <Field label="Vehicle number" required>
              <Input
                value={form.vehicle_number}
                onChange={(e) => setForm((f) => ({ ...f, vehicle_number: sanitizeVehicleNumberInput(e.target.value) }))}
                placeholder={VEHICLE_NUMBER_PLACEHOLDER}
                autoCapitalize="characters"
                className="min-h-11"
              />
            </Field>
            <Field label="Driver name">
              <Input
                value={form.driver_name}
                onChange={(e) => setForm((f) => ({ ...f, driver_name: e.target.value }))}
                className="min-h-11"
              />
            </Field>
            <Field label="Driver phone">
              <Input
                value={form.driver_phone}
                onChange={(e) => setForm((f) => ({ ...f, driver_phone: e.target.value }))}
                className="min-h-11"
              />
            </Field>
            <Field label="Transporter">
              <Input
                value={form.transporter_name}
                onChange={(e) => setForm((f) => ({ ...f, transporter_name: e.target.value }))}
                className="min-h-11"
              />
            </Field>
            <Field label="Expected arrival">
              <Input
                type="datetime-local"
                value={form.expected_arrival}
                onChange={(e) => setForm((f) => ({ ...f, expected_arrival: e.target.value }))}
                className="min-h-11"
              />
            </Field>
          </div>
          <Field label="Loading notes">
            <Textarea
              value={form.loading_notes}
              onChange={(e) => setForm((f) => ({ ...f, loading_notes: e.target.value }))}
              rows={2}
            />
          </Field>
          <div className="space-y-2">
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Sales contracts</Label>
            <div className="flex flex-wrap gap-2">
              {activeContracts.map((c) => {
                const on = rows.some((r) => String(r.sales_contract_id) === String(c.id));
                return (
                  <Button
                    key={c.id}
                    type="button"
                    size="sm"
                    variant={on ? "default" : "outline"}
                    className={on ? "bg-bansal-sage hover:bg-bansal-sageDark text-white" : ""}
                    onClick={() => toggleContract(c)}
                  >
                    {c.contract_number} · {c.variety}
                  </Button>
                );
              })}
            </div>
            {rows.map((r) => (
              <div key={r.sales_contract_id} className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-end">
                <p className="text-sm font-medium sm:col-span-2">
                  {r.contract_number} · {r.item_name}
                </p>
                <Field label="Expected weight (qtl)">
                  <Input
                    type="number"
                    inputMode="decimal"
                    value={r.expected_weight_qtl}
                    onChange={(e) => updateRow(r.sales_contract_id, { expected_weight_qtl: e.target.value })}
                    className="min-h-11"
                  />
                </Field>
              </div>
            ))}
          </div>
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" onClick={resetForm} disabled={busy}>
              Cancel
            </Button>
            <Button
              type="button"
              className="bg-bansal-sage hover:bg-bansal-sageDark text-white"
              onClick={submit}
              disabled={busy}
            >
              {busy ? "Saving…" : "Save booking"}
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading bookings…
        </div>
      ) : bookings.length === 0 ? (
        <p className="text-sm text-muted-foreground py-2">No active truck bookings.</p>
      ) : (
        <div className="space-y-2 mt-3">
          {bookings.map((b) => (
            <div
              key={b.id}
              className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-border px-3 py-3"
            >
              <div className="flex items-start gap-3 min-w-0">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Truck className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold tracking-wide">{b.vehicle_number}</span>
                    <Pill tone="sage">{b.code}</Pill>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {b.party_name || "—"} · {b.sales_contract_number || "—"}
                    {b.transporter_name ? ` · ${b.transporter_name}` : ""}
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Booked {formatListDate(b.booked_at)} · Expires {formatListDate(b.expires_at)}
                  </p>
                </div>
              </div>
              <Button type="button" variant="ghost" size="sm" onClick={() => cancelBooking(b.id)} disabled={busy}>
                Cancel
              </Button>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
        {label}
        {required ? <span className="text-red-500 ml-0.5">*</span> : null}
      </Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
